import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createElement } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { supabase } from '@/lib/supabase';
import { en } from '@/features/i18n/messages/en';
import { fr, type MessageKey } from '@/features/i18n/messages/fr';

export type Locale = 'fr' | 'en';
export type { MessageKey };

const dictionaries = { fr, en } as const;

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function interpolate(template: string, vars?: Record<string, string | number>) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const { profile, refreshProfile } = useAuth();
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('isbd_locale');
      if (saved === 'en' || saved === 'fr') return saved;
    }
    return 'fr';
  });

  useEffect(() => {
    if (profile?.preferred_locale === 'fr' || profile?.preferred_locale === 'en') {
      setLocaleState(profile.preferred_locale);
    }
  }, [profile?.preferred_locale]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = (next: Locale) => {
    setLocaleState(next);
    if (typeof window !== 'undefined') window.localStorage.setItem('isbd_locale', next);
    if (profile) {
      void supabase
        .from('profiles')
        .update({ preferred_locale: next })
        .eq('id', profile.id)
        .then(() => refreshProfile());
    }
  };

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => {
        const dict = dictionaries[locale] ?? fr;
        const fallback = fr[key] ?? key;
        return interpolate(dict[key] ?? fallback, vars);
      },
    }),
    [locale, profile?.id],
  );

  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
